import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import http from 'services/http';
import { httpStatusOf, smsApi, SmsParticipantDto } from 'services/camMirrorService';
import { useAppSelector } from 'app/hooks';
import { USER_ROLE } from 'constants/common';
import { SMS_MIN_PLAN_LABEL } from 'utils/smsPlanGate';
import './NotificationSettings.css';

/**
 * Settings > Notifications (tab 4). Replaces the older SMS-only screen.
 *
 * - Groups come from GET api/notifications/preferences (one card per group:
 *   In the app always on, Email and Text switches). Changes save right away
 *   (PUT, one switch at a time).
 * - "Chat messages by text" is the same switch as the old "In-App Messaging SMS"
 *   row (cam-mirror sms-preference), so everyone keeps what they chose before.
 * - Texts need a verified phone and, for students, Silver Plus or Gold. Until a
 *   person changes a text switch here, texts follow their earlier "GradMap
 *   Updates SMS" consent (the backend reads it).
 */

type Channel = 'email' | 'sms';
interface ChannelPref {
  channel: Channel;
  enabled: boolean;
}
interface GroupPref {
  group: string;
  name: string;
  examples: string[];
  bell: boolean;
  channels: ChannelPref[];
}
interface Texts {
  phone: boolean;
  phone_verified: boolean;
  plan_ok: boolean;
}
interface PrefsResponse {
  groups: GroupPref[];
  texts: Texts;
}

const errorText = (e: any) => {
  const m = e?.response?.data?.message;
  return Array.isArray(m) ? m.join(' ') : typeof m === 'string' ? m : '';
};

function Switch({ on, disabled, label, onChange }: { on: boolean; disabled?: boolean; label: string; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      className={`gm-ns-switch${on ? ' on' : ''}`}
      onClick={() => onChange(!on)}
    />
  );
}

const NotificationSettings = () => {
  const history = useHistory();
  const personal = useAppSelector((s) => s.basicInformation.myBasicInformation);
  const userId = personal?.student_id;
  const role = personal?.user_role ?? sessionStorage.getItem('userRole') ?? USER_ROLE.student;
  const isStudent = role === USER_ROLE.student;

  const [data, setData] = useState<PrefsResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [chat, setChat] = useState<SmsParticipantDto | null>(null);
  const [chatBusy, setChatBusy] = useState(false);
  const [upgrade, setUpgrade] = useState(false);

  const load = useCallback(() => {
    setFailed(false);
    (http.get('api/notifications/preferences') as Promise<PrefsResponse>)
      .then((r) => setData(r && Array.isArray(r.groups) ? r : { groups: [], texts: { phone: false, phone_verified: false, plan_ok: false } }))
      .catch(() => setFailed(true));
    if (userId) {
      smsApi
        .getParticipant(userId)
        .then(setChat)
        .catch(() => setChat(null));
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const textsBlocked = useMemo(() => {
    const t = data?.texts;
    if (!t) return '';
    if (!t.plan_ok) return `Texts come with ${SMS_MIN_PLAN_LABEL} or Gold. Bell and email reminders are always free.`;
    if (!t.phone) return 'Add a phone number in My Profile to get texts.';
    if (!t.phone_verified) return 'Verify your phone number in My Profile to get texts.';
    return '';
  }, [data]);

  const setPref = async (group: string, channel: Channel, enabled: boolean) => {
    if (!data) return;
    const key = `${group}|${channel}`;
    if (busy) return;
    if (channel === 'sms' && enabled && data.texts && !data.texts.plan_ok) {
      setUpgrade(true);
      return;
    }
    const before = data;
    setData({
      ...data,
      groups: data.groups.map((g) =>
        g.group !== group ? g : { ...g, channels: g.channels.map((c) => (c.channel === channel ? { ...c, enabled } : c)) },
      ),
    });
    setBusy(key);
    try {
      await http.put('api/notifications/preferences', { group, channel, enabled });
      toast.success('Saved');
    } catch (e) {
      setData(before);
      if (httpStatusOf(e) === 403) setUpgrade(true);
      else if (!(e as any)?.response) toast.error(errorText(e) || 'Could not save. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const setChatText = async (enabled: boolean) => {
    if (chatBusy || !chat) return;
    if (enabled && !chat.smsAllowed) {
      setUpgrade(true);
      return;
    }
    const before = chat;
    setChat({ ...chat, smsReceiveEnabled: enabled });
    setChatBusy(true);
    try {
      setChat(await smsApi.setPreference(enabled));
      toast.success('Saved');
    } catch (e) {
      setChat(before);
      if (httpStatusOf(e) === 403) setUpgrade(true);
    } finally {
      setChatBusy(false);
    }
  };

  const cell = (g: GroupPref, channel: Channel) => {
    const c = g.channels.find((x) => x.channel === channel);
    if (!c) return <span className="gm-ns-always">Not used</span>;
    const blocked = channel === 'sms' && !!textsBlocked;
    const label = `${g.name}: ${channel === 'sms' ? 'Text' : 'Email'}`;
    if (blocked && data?.texts.plan_ok === false && isStudent) {
      return (
        <button type="button" className="gm-ns-lock" onClick={() => setUpgrade(true)} aria-label={`${label}, comes with ${SMS_MIN_PLAN_LABEL} or Gold`}>
          {SMS_MIN_PLAN_LABEL}
        </button>
      );
    }
    return <Switch on={c.enabled && !blocked} disabled={blocked || busy === `${g.group}|${channel}`} label={label} onChange={(v) => setPref(g.group, channel, v)} />;
  };

  if (failed) {
    return (
      <div className="gm-ns">
        <h2>Notifications</h2>
        <div className="gm-ns-note">We could not load your settings. <button type="button" className="gm-ns-link" onClick={load}>Try again</button></div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="gm-ns">
        <h2>Notifications</h2>
        <p className="gm-ns-sub">Loading your settings...</p>
      </div>
    );
  }

  const chatLocked = !!chat && !chat.smsAllowed && !chat.smsReceiveEnabled;

  return (
    <div className="gm-ns">
      <h2>Notifications</h2>
      <p className="gm-ns-sub">Choose how GradMap reaches you. Changes save right away.</p>
      {textsBlocked && <div className="gm-ns-note gm-ns-top">{textsBlocked}</div>}

      {data.groups.map((g) => (
        <section className="gm-ns-card" key={g.group}>
          <div className="gm-ns-head">
            <h3>{g.name}</h3>
            <span className="gm-ns-col b">In the app</span>
            <span className="gm-ns-col">Email</span>
            <span className="gm-ns-col">Text</span>
          </div>
          <div className="gm-ns-row">
            <ul className="gm-ns-names">
              {g.examples.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
            <div className="gm-ns-cell b">
              <span className="gm-ns-always">{g.bell ? 'Always on' : 'Not used'}</span>
            </div>
            <div className="gm-ns-cell">{cell(g, 'email')}</div>
            <div className="gm-ns-cell">{cell(g, 'sms')}</div>
          </div>
        </section>
      ))}

      {chat && (
        <section className="gm-ns-card">
          <div className="gm-ns-head">
            <h3>Chat messages by text</h3>
          </div>
          <div className="gm-ns-row gm-ns-row-2">
            <p className="gm-ns-desc">
              Get a text when someone sends you a message in GradMap, and reply by text.
              {chatLocked ? ` Comes with ${SMS_MIN_PLAN_LABEL} or Gold.` : ''}
            </p>
            <div className="gm-ns-cell">
              {chatLocked ? (
                <button type="button" className="gm-ns-lock" onClick={() => setUpgrade(true)}>
                  {SMS_MIN_PLAN_LABEL}
                </button>
              ) : (
                <Switch on={!!chat.smsReceiveEnabled} disabled={chatBusy} label="Chat messages by text" onChange={setChatText} />
              )}
            </div>
          </div>
        </section>
      )}

      <section className="gm-ns-card">
        <h3>Account</h3>
        <p className="gm-ns-desc">Always sent by email: your welcome email and sign-in and security codes. These can't be turned off.</p>
      </section>

      <div className="gm-ns-note">
        By turning on texts, you agree to get text messages from GradMap at the phone number in your profile. Message frequency varies;
        message and data rates may apply. Texts never go out between 8 pm and 8 am in your time zone. Reply STOP to any text to opt out.
      </div>

      {upgrade && (
        <div className="gm-ns-dialog" role="dialog" aria-modal="true" aria-labelledby="gm-ns-up-title" onClick={() => setUpgrade(false)}>
          <div className="gm-ns-dialog-box" onClick={(e) => e.stopPropagation()}>
            <h3 id="gm-ns-up-title">Texts come with {SMS_MIN_PLAN_LABEL} or Gold</h3>
            <p className="gm-ns-desc">You'll still get every reminder in the app and by email on your current plan.</p>
            <div className="gm-ns-actions">
              <button type="button" className="gm-ns-btn quiet" onClick={() => setUpgrade(false)}>
                Not now
              </button>
              {isStudent && (
                <button
                  type="button"
                  className="gm-ns-btn"
                  onClick={() => {
                    setUpgrade(false);
                    history.push(`/settings?active-tab=5`); // Plan and billing
                  }}
                >
                  See plans
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;
